import { useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import {
  IMPORT_EXTENSIONS,
  importMaxBytes,
  ImportUploadError,
  useCreateImport,
} from "../api/imports";
import type { ImportLicenceChoice } from "../api/types";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Card, Field, Input, PageHeader, Select } from "../ui";
import { SectionHeader } from "./SectionHeader";

type Choice = ImportLicenceChoice["choice"];

export const IMPORT_FILE_HELPER =
  "A .glb, .fbx or .obj file up to 100 MB, or a .zip of the model with its textures up to 300 MB.";
export const IMPORT_PUT_FAILED = "The upload did not finish. Try again.";
/** The longest name: the row holds 120 characters with the extension. */
const NAME_MAX = 116;
const ADDRESS_MAX = 300;

const CHOICES: { id: Choice; label: string }[] = [
  { id: "own", label: "We made it, or it was made for us" },
  { id: "cc0", label: "Free to use with no credit (CC0)" },
  { id: "marketplace", label: "Bought from a marketplace (royalty-free)" },
  { id: "other", label: "Another licence that allows advertising use" },
];

const MARKETPLACES: { id: string; label: string }[] = [
  { id: "cgtrader", label: "CGTrader" },
  { id: "turbosquid", label: "TurboSquid" },
  { id: "sketchfab", label: "Sketchfab" },
  { id: "fab", label: "Fab" },
  { id: "other", label: "Other" },
];

/** A file name's extension in lower case, and what comes before it. */
function parts(fileName: string): { stem: string; extension: string } {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0) return { stem: fileName, extension: "" };
  return {
    stem: fileName.slice(0, dot),
    extension: fileName.slice(dot + 1).toLowerCase(),
  };
}

/** Whether the file can be sent at all: its format and its size. */
function sendable(file: File): boolean {
  const { extension } = parts(file.name);
  return (
    (IMPORT_EXTENSIONS as readonly string[]).includes(extension) &&
    file.size >= 1 &&
    file.size <= importMaxBytes(extension)
  );
}

const CHECK_ROW =
  "flex min-h-[44px] items-center gap-2 text-sm text-slate-800 sm:min-h-0";

/** `/assets/import`: one model file, what it is called, where it is from, and
 *  the user's word that it may be used in advertising. The file goes straight
 *  to storage; the page then moves on to the model's own page. */
export function ImportPage() {
  const navigate = useNavigate();
  const create = useCreateImport();
  const formId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [choice, setChoice] = useState<Choice | null>(null);
  const [marketplace, setMarketplace] = useState("");
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);

  const working = create.isPending;
  const address = choice ? (addresses[choice] ?? "").trim() : "";
  const fileOk = file !== null && sendable(file);
  const choiceOk =
    choice === "own" ||
    choice === "cc0" ||
    (choice === "marketplace" && marketplace !== "" && address !== "") ||
    (choice === "other" && address !== "");
  const ready = fileOk && name.trim() !== "" && choiceOk && confirmed;

  const submit = () => {
    if (!ready || working || !file || !choice) return;
    const license: ImportLicenceChoice = { choice };
    if (choice === "marketplace") license.marketplace = marketplace;
    if (choice !== "own" && address) license.url = address;
    create.mutate(
      {
        file,
        name: `${name.trim()}.${parts(file.name).extension}`,
        license,
      },
      {
        onSuccess: (id) =>
          navigate(`/assets/imports/${encodeURIComponent(id)}`),
      },
    );
  };

  const addressField = (label: string, optional: boolean) => (
    <Field label={label} optional={optional} className="ml-6">
      <Input
        type="url"
        inputMode="url"
        placeholder="https://"
        maxLength={ADDRESS_MAX}
        className="min-h-[44px] sm:min-h-0"
        value={choice ? (addresses[choice] ?? "") : ""}
        onChange={(event) =>
          setAddresses({ ...addresses, [choice ?? ""]: event.target.value })
        }
      />
    </Field>
  );

  const failure = create.error;
  const percent = Math.round(Math.min(1, Math.max(0, create.progress)) * 100);

  return (
    <>
      <PageHeader eyebrow="— Objects" title="Import a model" />
      <SectionHeader />
      <Card className="max-w-2xl p-4 sm:p-6">
        <form
          id={formId}
          noValidate
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <fieldset
            disabled={working}
            className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0"
          >
            <div className="flex flex-col gap-1">
              <Field
                label="Model file"
                helper={IMPORT_FILE_HELPER}
                error={file !== null && !fileOk ? IMPORT_FILE_HELPER : null}
              >
                <input
                  type="file"
                  accept={IMPORT_EXTENSIONS.map((e) => `.${e}`).join(",")}
                  className="block min-h-[44px] w-full max-w-full text-sm text-slate-700 sm:min-h-0"
                  onChange={(event) => {
                    const picked = event.target.files?.[0] ?? null;
                    setFile(picked);
                    if (picked) {
                      setName(parts(picked.name).stem.slice(0, NAME_MAX));
                    }
                    create.reset();
                  }}
                />
              </Field>
              <Link
                to="/help/3d-library#own-model"
                className="inline-flex min-h-[44px] items-center self-start text-sm underline sm:min-h-0"
              >
                How to export a .glb from Blender
              </Link>
            </div>

            <Field label="Name">
              <Input
                required
                maxLength={NAME_MAX}
                className="min-h-[44px] sm:min-h-0"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>

            <fieldset className="m-0 flex min-w-0 flex-col gap-1 border-0 p-0">
              <legend className="tb-field-label mb-1 p-0">
                Where is it from?
              </legend>
              {CHOICES.map((option) => (
                <div key={option.id} className="flex flex-col gap-2">
                  <label className={CHECK_ROW}>
                    <input
                      type="radio"
                      name={`${formId}-from`}
                      checked={choice === option.id}
                      onChange={() => setChoice(option.id)}
                    />
                    {option.label}
                  </label>
                  {choice === option.id && option.id === "cc0"
                    ? addressField("Page it came from", true)
                    : null}
                  {choice === option.id && option.id === "marketplace" ? (
                    <>
                      <Field label="Marketplace" className="ml-6">
                        <Select
                          required
                          className="min-h-[44px] sm:min-h-0"
                          value={marketplace}
                          onChange={(event) =>
                            setMarketplace(event.target.value)
                          }
                        >
                          <option value="">Choose one</option>
                          {MARKETPLACES.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      {addressField("Listing address", false)}
                    </>
                  ) : null}
                  {choice === option.id && option.id === "other"
                    ? addressField("Licence page address", false)
                    : null}
                </div>
              ))}
              <p className="m-0 mt-1 text-xs text-slate-600">
                Models that require a credit line (for example CC-BY) cannot be
                used: an ad cannot carry a credit.
              </p>
            </fieldset>

            <label className={CHECK_ROW}>
              <input
                type="checkbox"
                required
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              I have the right to use this model in advertising.
            </label>
          </fieldset>

          {working ? (
            <div className="flex flex-col gap-3" data-testid="import-working">
              <div className="flex items-center gap-3">
                <div
                  role="progressbar"
                  aria-label="Uploading your model"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  className="h-2 min-w-0 flex-1 overflow-hidden rounded-pill bg-slate-200"
                >
                  <div
                    className="h-full rounded-pill bg-brand transition-[width]"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <span className="w-10 text-right text-sm tabular-nums text-slate-700">
                  {percent}%
                </span>
              </div>
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[44px] sm:min-h-0"
                  onClick={create.cancel}
                >
                  Cancel upload
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                type="submit"
                variant="primary"
                className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                disabled={!ready}
              >
                Upload and check
              </Button>
            </div>
          )}

          {working || !failure ? null : failure instanceof ApiError ? (
            <ErrorLine error={failure} context="import" />
          ) : failure instanceof ImportUploadError &&
            failure.code === "cancelled" ? null : (
            <p role="alert" className="m-0 text-sm text-slate-700">
              {IMPORT_PUT_FAILED}
            </p>
          )}
        </form>
      </Card>
    </>
  );
}
